import { useEffect, useMemo, useRef } from "react";
import { Alert, Box, CircularProgress, Paper, Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import * as echarts from "echarts/core";
import { LineChart } from "echarts/charts";
import { DataZoomComponent, GridComponent, LegendComponent, TooltipComponent } from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import type { EChartsCoreOption } from "echarts/core";
import { formatDateTime, type DisplayLocalization } from "../../../../lib/datetime/displayDateTime";
import type { DashboardSeverityTimeline } from "../../../../services/dashboardOpenSearchService";
import { geminiChartPalette } from "../../../../assets/theme/geminiPalette";

echarts.use([LineChart, DataZoomComponent, GridComponent, LegendComponent, TooltipComponent, CanvasRenderer]);

const CHART_HEIGHT = 320;

type SeverityLineKey = "low" | "medium" | "high" | "critical";

type DashboardSeverityTimelineChartProps = {
  timeline?: DashboardSeverityTimeline;
  loading?: boolean;
  error?: string | null;
  localization: DisplayLocalization;
  windowLabel: string;
};

function SeverityTimelineEChart({ option }: { option: EChartsCoreOption }) {
  const hostRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const chart = echarts.init(host);
    chartRef.current = chart;
    const observer = new ResizeObserver(() => chart.resize());
    observer.observe(host);

    return () => {
      observer.disconnect();
      chart.dispose();
      chartRef.current = null;
    };
  }, []);

  useEffect(() => {
    chartRef.current?.setOption(option, true);
  }, [option]);

  return <Box ref={hostRef} sx={{ width: "100%", height: CHART_HEIGHT }} />;
}

export default function DashboardSeverityTimelineChart({
  timeline,
  loading = false,
  error = null,
  localization,
  windowLabel,
}: DashboardSeverityTimelineChartProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const chartColors = geminiChartPalette(theme.palette.mode === "dark" ? "dark" : "light");
  const series = useMemo(
    () =>
      [
        { key: "low" as const, label: t("dashboard.low"), color: theme.palette.success.main },
        { key: "medium" as const, label: t("dashboard.medium"), color: theme.palette.info.main },
        { key: "high" as const, label: t("dashboard.high"), color: theme.palette.warning.main },
        { key: "critical" as const, label: t("dashboard.critical"), color: theme.palette.error.main },
      ] satisfies Array<{ key: SeverityLineKey; label: string; color: string }>,
    [t, theme.palette.error.main, theme.palette.info.main, theme.palette.success.main, theme.palette.warning.main],
  );

  const points = timeline?.points;
  const option = useMemo<EChartsCoreOption>(() => {
    const seriesPoints = points ?? [];
    const categories = seriesPoints.map((point) => formatDateTime(point.timestamp, "time", localization));
    const tooltipText = theme.palette.text.primary;
    const tooltipBg = theme.palette.background.paper;

    return {
      color: series.map((item) => item.color),
      animationDuration: 280,
      grid: { left: 8, right: 12, top: 36, bottom: 56, containLabel: true },
      legend: {
        top: 0,
        left: 0,
        icon: "roundRect",
        itemWidth: 14,
        itemHeight: 8,
        textStyle: { color: chartColors.text, fontSize: 12 },
      },
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "line" },
        backgroundColor: tooltipBg,
        borderColor: theme.palette.divider,
        textStyle: { color: tooltipText },
        formatter: (raw: unknown) => {
          const items = (Array.isArray(raw) ? raw : [raw]) as Array<{
            axisValue?: string | number;
            marker?: string;
            seriesName?: string;
            data?: unknown;
          }>;
          const title = String(items[0]?.axisValue ?? "");
          const lines = items.map((item) => `${item.marker ?? ""}${item.seriesName}: <b>${item.data ?? 0}</b>`);
          return [title, ...lines].join("<br/>");
        },
      },
      dataZoom: [
        {
          type: "slider",
          start: 0,
          end: 100,
          height: 18,
          bottom: 8,
          borderColor: chartColors.axis,
          fillerColor: theme.palette.mode === "dark" ? "rgba(138,180,248,0.18)" : "rgba(26,115,232,0.12)",
          handleStyle: { color: chartColors.seriesBlue },
          textStyle: { color: chartColors.textMuted },
        },
        { type: "inside" },
      ],
      xAxis: {
        type: "category",
        data: categories,
        boundaryGap: false,
        axisLine: { lineStyle: { color: chartColors.axis } },
        axisLabel: {
          hideOverlap: true,
          color: chartColors.textMuted,
        },
        splitLine: { show: false },
      },
      yAxis: {
        type: "value",
        min: 0,
        minInterval: 1,
        axisLabel: { color: chartColors.textMuted },
        splitLine: { lineStyle: { color: chartColors.splitLine } },
      },
      series: series.map((item) => ({
        name: item.label,
        type: "line",
        data: seriesPoints.map((point) => point[item.key]),
        showSymbol: false,
        smooth: false,
        emphasis: { focus: "series" },
        lineStyle: { width: 2, color: item.color },
        itemStyle: { color: item.color },
      })),
    };
  }, [
    chartColors,
    localization,
    points,
    series,
    theme.palette.background.paper,
    theme.palette.divider,
    theme.palette.mode,
    theme.palette.text.primary,
  ]);

  return (
    <Box>
      <Typography
        variant="overline"
        sx={{
          letterSpacing: "0.08em",
          color: "text.secondary",
          display: "block",
          mb: 0.75,
          fontWeight: 600,
        }}
      >
        {t("dashboard.severityTimeline")} · {windowLabel}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 0.75, mt: -0.25, lineHeight: 1.35 }}
      >
        {t("dashboard.severityTimelineHint")}
      </Typography>
      <Paper
        elevation={0}
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          px: 1.25,
          py: 1.25,
          minHeight: CHART_HEIGHT,
          display: "flex",
          alignItems: "stretch",
        }}
      >
        {loading ? (
          <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ width: 1 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">
              {t("dashboard.severityTimelineLoading")}
            </Typography>
          </Stack>
        ) : error ? (
          <Alert severity="error" sx={{ width: 1, alignSelf: "center" }}>
            {t("dashboard.severityTimelineFailed")}: {error}
          </Alert>
        ) : !points || points.length === 0 ? (
          <Stack alignItems="center" justifyContent="center" sx={{ width: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t("dashboard.severityTimelineEmpty")}
            </Typography>
          </Stack>
        ) : (
          <Box sx={{ width: 1 }}>
            <SeverityTimelineEChart option={option} />
          </Box>
        )}
      </Paper>
    </Box>
  );
}
